import clsx from 'clsx';
import { useNavigate } from 'react-router';
import { Avatar, Icon } from '@/design-system';
import { ns, useT } from '@/i18n';
import { useSession } from '@/services/queries';
import { paths } from '../paths';
import { useOverlays } from '../state/overlays';
import type { ShellHandle } from './shellConfig';
import { useUnreadCount } from './useUnread';
import styles from './BottomNav.module.css';

const INDEX = { explore: 0, favourites: 1, trips: 3, profile: 4 } as const;

/** Floating mobile bottom bar (`.bn`): Explorer · Favoris · search FAB · Voyages · Profil. */
export function BottomNav({ handle, mini }: { handle: ShellHandle; mini: boolean }) {
  const navigate = useNavigate();
  const t = useT(ns.shell);
  const tc = useT(ns.common);
  const { open } = useOverlays();
  const { data: user } = useSession();
  const unread = useUnreadCount();
  const active = handle.tab ? INDEX[handle.tab] : -1;
  const isExp = handle.universe === 'exp';
  const item = (i: number) => clsx(styles.item, active === i && styles.on);

  return (
    <nav
      className={clsx(styles.bn, active < 0 ? styles.tn : styles[`t${active}`], mini && styles.mini)}
      aria-label={t('nav.label')}
    >
      <span className={styles.ind} aria-hidden="true" />
      <button
        type="button"
        className={item(0)}
        aria-current={active === 0 ? 'page' : undefined}
        onClick={() => navigate(isExp ? paths.experiences : paths.home)}
      >
        <span className={styles.ic}>
          <Icon name="compass" size={23} />
        </span>
        <span className={styles.lbl}>{t('nav.explore')}</span>
      </button>
      <button
        type="button"
        className={item(1)}
        aria-current={active === 1 ? 'page' : undefined}
        onClick={() => navigate(paths.favourites)}
      >
        <span className={styles.ic}>
          <Icon name="heart" size={23} />
        </span>
        <span className={styles.lbl}>{tc('favourites')}</span>
      </button>
      <button
        type="button"
        className={styles.fab}
        aria-label={isExp ? t('nav.searchExperience') : t('nav.searchVehicle')}
        onClick={() => open(isExp ? 'experienceSearch' : 'vehicleSearch')}
      >
        <Icon name="search" size={25} strokeWidth={2.5} />
      </button>
      <button
        type="button"
        className={item(3)}
        aria-current={active === 3 ? 'page' : undefined}
        onClick={() => navigate(paths.trips)}
      >
        <span className={styles.ic}>
          <Icon name="briefcase" size={23} />
        </span>
        <span className={styles.lbl}>{t('nav.trips')}</span>
      </button>
      <button
        type="button"
        className={item(4)}
        aria-current={active === 4 ? 'page' : undefined}
        aria-label={t('nav.profileUnread', { count: unread })}
        onClick={() => navigate(paths.profile)}
      >
        <span className={styles.ic}>
          {user ? <Avatar name={user.name} size={25} dot={unread > 0} /> : <Icon name="user" size={23} />}
        </span>
        <span className={styles.lbl}>{tc('profile')}</span>
      </button>
    </nav>
  );
}
